import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  getGetMyStudyParticipantsMeStudyGetQueryKey,
  getListMyLegalDocumentsParticipantsMeLegalDocumentsGetQueryKey,
  type ParticipantStudy,
  useUpdateParticipantLanguageParticipantsLanguageUpdatePut,
} from "@/api-v3";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import {
  changeUserLanguage,
  getRegionById,
} from "@/features/localisation/i18n";
import { noop } from "@/lib/utils";
import { useIsLogin } from "@/router/hooks/use-is-login";

import { useStudyLanguages } from "../hooks/use-study-languages";
import { includesLanguage } from "../utils";

const languageSelectedOnLoginKey = "languageSelectedOnLogin";

type LanguageRegions = Record<string, string[]>;

const languageRegions: LanguageRegions = {
  Europe: [
    "bg", // Bulgarian
    "bs", // Bosnian
    "ca", // Catalan
    "cs", // Czech
    "da", // Danish
    "de", // German
    "el", // Greek
    "es-ES", // Spanish (region-coded: Spain)
    "et", // Estonian
    "fi", // Finnish
    "fr", // French
    "hr", // Croatian
    "hu", // Hungarian
    "hy", // Armenian
    "it", // Italian
    "ka", // Georgian
    "lt", // Lithuanian
    "lv", // Latvian
    "nl", // Dutch
    "no", // Norwegian
    "pl", // Polish
    "pt", // Portuguese
    "ro", // Romanian
    "ru", // Russian
    "sk", // Slovak
    "sr", // Serbian
    "sr-Cyrl", // Serbian (Cyrillic)
    "sv", // Swedish
    "tr", // Turkish
    "uk", // Ukrainian
  ],
  "Asia-Pacific": [
    "hi", // Hindi
    "ja", // Japanese
    "kk", // Kazakh
    "ko", // Korean
    "th", // Thai
    "ur", // Urdu
    "vi", // Vietnamese
    "zh", // Chinese (Simplified)
    "zf", // Traditional Chinese
    "bn", // Bengali
  ],
  "Middle East and Africa": [
    "af", // Afrikaans
    "ar", // Arabic
    "he", // Hebrew
    "st", // Southern Sotho
    "xh", // Xhosa
    "zu", // Zulu
    "nso", // Northern Sotho
    "ts", // Tsonga
    "or", // Odia (Oriya)
    "te", // Telugu
  ],
  Americas: [
    "en", // English
    "es-419", // Spanish (Latin America)
    "fr-CA", // French (Canada)
  ],
};

interface LanguageSelectorDialogProps {
  open: boolean;
  forcedSelection: boolean;
  hideDialog: () => void;
}

export function LanguageSelectorDialog({
  open,
  forcedSelection,
  hideDialog,
}: LanguageSelectorDialogProps) {
  const { i18n, t } = useTranslation();
  const queryClient = useQueryClient();

  const { studyLanguages } = useStudyLanguages(forcedSelection && open);
  const isLogin = useIsLogin();

  const { mutate } =
    useUpdateParticipantLanguageParticipantsLanguageUpdatePut();

  const handleLanguageSelectClick = async (selectedLanguage: string) => {
    if (isLogin) {
      localStorage.setItem(languageSelectedOnLoginKey, selectedLanguage);
    } else {
      localStorage.removeItem(languageSelectedOnLoginKey);
    }

    await handleLanguageSelect(selectedLanguage);
  };

  const handleLanguageSelect = async (selectedLanguage: string) => {
    await changeUserLanguage(selectedLanguage);
    if (!isLogin || forcedSelection) {
      mutate({ data: { preferred_lang: selectedLanguage } });
    }
    // A query still on its first fetch keeps that request through an
    // invalidation, so cancel it first or it keeps the previous language.
    if (isLogin) {
      const queryKey =
        getListMyLegalDocumentsParticipantsMeLegalDocumentsGetQueryKey();
      await queryClient.cancelQueries({ queryKey });
      await queryClient.invalidateQueries({ queryKey });
      hideDialog();
    } else {
      await queryClient.cancelQueries();
      await queryClient.invalidateQueries();

      // Read the study refetched above. The languages from the last render are
      // missing when the choice is made before the study has loaded.
      const study = queryClient.getQueryData<ParticipantStudy>(
        getGetMyStudyParticipantsMeStudyGetQueryKey()
      );
      if (!includesLanguage(study?.languages ?? [], selectedLanguage)) return;
      hideDialog();
    }
  };

  const filterLanguages = (regionLanguages: string[]): string[] => {
    if (studyLanguages != null && studyLanguages.length > 0) {
      return regionLanguages.filter((langId) =>
        // Study languages are lower cased when retrieved from the API on use-study-languages hook.
        studyLanguages?.includes(langId.toLowerCase())
      );
    }
    const supportedLngs = Array.isArray(i18n.options?.supportedLngs)
      ? i18n.options.supportedLngs
      : [];

    return regionLanguages.filter((langId) => supportedLngs.includes(langId));
  };

  interface Group {
    region: string;
    filteredLanguages: string[];
  }
  const renderLanguageByRegion = () => {
    const isGroup = (group: Group | null): group is Group => group !== null;

    const regionsWithLanguages: Group[] = Object.keys(languageRegions)
      .map((region) => {
        const filteredLanguages = filterLanguages(languageRegions[region]);

        if (filteredLanguages.length === 0) return null; // Skip empty regions

        return { region, filteredLanguages }; // Return region and its filtered languages
      })
      .filter(isGroup); // Remove null values (regions with no languages)

    const manyRegions = regionsWithLanguages.length > 1;
    // Render regions with languages
    return regionsWithLanguages.map(({ region, filteredLanguages }, index) => (
      <div key={region}>
        {index !== 0 && <Separator className="my-4" />}
        {manyRegions && <h6 className="text-lg font-bold mb-2">{region}</h6>}
        <div className="flex flex-wrap min-w-0 gap-4">
          {filteredLanguages.map((languageId) => {
            const language = getRegionById(languageId);
            if (!language) return null;

            return (
              <div
                className="w-full sm:w-[calc(50%-8px)] md:w-[calc(33.333%-10.6666666667px)]"
                key={language.id}
              >
                <ul>
                  <li>
                    <div
                      tabIndex={0}
                      onKeyDown={noop}
                      role="button"
                      className="cursor-pointer px-4 py-2 hover:underline"
                      onClick={async () => {
                        await handleLanguageSelectClick(language.id);
                      }}
                      data-testid={`language-${language.name}-button`}
                    >
                      <span
                        className={`${
                          language.id === i18n.language ? "text-primary" : ""
                        }`}
                      >
                        {language.name}
                      </span>
                    </div>
                  </li>
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    ));
  };

  return (
    <Dialog open={open} onOpenChange={hideDialog}>
      <DialogContent className="max-w-3xl" dismissible={!forcedSelection}>
        <DialogHeader>
          <DialogTitle className="text-center">
            {t("trialLanguage_title")}
          </DialogTitle>
        </DialogHeader>

        <div
          className="overflow-y-auto max-h-[80vh]"
          data-testid="language-selector"
        >
          {renderLanguageByRegion()}
        </div>
      </DialogContent>
    </Dialog>
  );
}
